// Cota do Assistente Geral (Frente 3, G09/G10) — endpoints próprios `/assistant/quota/...`
// e os admin `/admin/users/<id>/general-quota/`. Separado do Hub (tabela D7).

import apiService from './api.service';
import {
  AdminGeneralQuota,
  GeneralQuotaChangeLog,
  GeneralQuotaIncreaseRequest,
  MyGeneralQuota,
  UpdateGeneralQuotaPayload,
} from '@/types/assistant';

type Paginated<T> = T[] | { results: T[] };

function unwrapList<T>(data: Paginated<T>): T[] {
  return Array.isArray(data) ? data : data?.results ?? [];
}

class AssistantQuotaService {
  // --- user ---

  async getMyQuota(): Promise<MyGeneralQuota> {
    const response = await apiService.get<MyGeneralQuota>('/assistant/quota/me/');
    return response.data;
  }

  async requestIncrease(payload: {
    reason: string;
    requested_field: string;
    suggested_value?: number;
  }): Promise<void> {
    await apiService.post('/assistant/quota/requests/', payload);
  }

  // --- admin (aba "Cota do Assistente" no form de usuário) ---

  async getUserQuota(userId: string): Promise<AdminGeneralQuota> {
    const response = await apiService.get<AdminGeneralQuota>(
      `/admin/users/${userId}/general-quota/`,
    );
    return response.data;
  }

  async updateUserQuota(
    userId: string,
    payload: UpdateGeneralQuotaPayload,
  ): Promise<AdminGeneralQuota> {
    const response = await apiService.patch<AdminGeneralQuota>(
      `/admin/users/${userId}/general-quota/`,
      payload,
    );
    return response.data;
  }

  async resetUserQuotaCounters(userId: string): Promise<AdminGeneralQuota> {
    const response = await apiService.post<AdminGeneralQuota>(
      `/admin/users/${userId}/general-quota/reset/`,
    );
    return response.data;
  }

  async getUserQuotaAudit(userId: string): Promise<GeneralQuotaChangeLog[]> {
    const response = await apiService.get<GeneralQuotaChangeLog[]>(
      `/admin/users/${userId}/general-quota/audit/`,
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  // --- admin: fila de pedidos de aumento de cota do Assistente ---
  // Tabela separada do Hub; a fila admin unificada (/admin/agentes/pedidos)
  // junta estes pedidos com os do Hub.

  async listIncreaseRequests(
    filters: { status?: string } = {},
  ): Promise<GeneralQuotaIncreaseRequest[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    const response = await apiService.get<Paginated<GeneralQuotaIncreaseRequest>>(
      `/assistant/quota/requests/${qs ? `?${qs}` : ''}`,
    );
    return unwrapList(response.data);
  }

  async approveIncreaseRequest(
    id: string,
    newValue: number,
    notes: string,
  ): Promise<GeneralQuotaIncreaseRequest> {
    const response = await apiService.post<GeneralQuotaIncreaseRequest>(
      `/assistant/quota/requests/${id}/approve/`,
      { new_value: newValue, notes },
    );
    return response.data;
  }

  async rejectIncreaseRequest(id: string, notes: string): Promise<GeneralQuotaIncreaseRequest> {
    const response = await apiService.post<GeneralQuotaIncreaseRequest>(
      `/assistant/quota/requests/${id}/reject/`,
      { notes },
    );
    return response.data;
  }
}

export default new AssistantQuotaService();
